import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * "Mark transfer sent" modal — buyer self-reports that they've sent the
 * bank transfer. Fires backend POST /api/client/lead-packs/{id}/transfer-sent
 * which notifies ops via email + SMS so the team can pre-match against the
 * bank inbox.
 *
 * Idempotent — calling twice is safe; the server returns the existing record.
 */
export default function MarkTransferSentModal({ open, onClose, order, onReported }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const [bankRef, setBankRef] = useState("");
  const [date, setDate]       = useState(todayISO);
  const [notes, setNotes]     = useState("");
  const [busy, setBusy]       = useState(false);

  // Reset state whenever modal opens for a different order
  useEffect(() => {
    if (open) {
      setBankRef("");
      setDate(todayISO);
      setNotes("");
    }
    // eslint-disable-next-line
  }, [open, order?.id]);

  const submit = async () => {
    if (!order?.id) return;
    const ref = bankRef.trim();
    if (ref.length < 2) {
      toast.error("Please enter the bank reference you used.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/client/lead-packs/${order.id}/transfer-sent`,
        { bank_reference: ref, transfer_date: date, notes: notes.trim() },
      );
      if (data?.already_reported) {
        toast.info("Transfer already reported — we're on it.");
      } else {
        toast.success("Thanks — our team has been notified and will pre-match against your transfer.");
      }
      onReported?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not report transfer — please try again or email billing@connex.is");
    } finally {
      setBusy(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="mark-transfer-sent-modal">
        <DialogHeader>
          <DialogTitle className="text-xl tracking-tight">Mark transfer sent</DialogTitle>
          <DialogDescription>
            Let our team know — we'll pre-match your reference against the bank inbox so your campaign goes live the moment funds clear.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3" data-testid="mtsm-summary">
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">Order</div>
            <div className="mt-0.5 text-[13.5px] font-medium text-slate-900">
              {order.quantity_ordered} × {order.sector_name}
            </div>
            <div className="text-[12px] text-slate-600">
              Invoice <span className="font-mono">{order.invoice_number}</span> · £{Number(order.total_gbp).toLocaleString()}
            </div>
          </div>

          <div>
            <Label className="text-[12px] text-slate-700">Bank reference you used *</Label>
            <Input
              value={bankRef}
              onChange={(e) => setBankRef(e.target.value)}
              placeholder={order.invoice_number || "CXG-PACK-…"}
              maxLength={64}
              data-testid="mtsm-bank-ref"
            />
            <p className="mt-1 text-[11px] text-slate-500">
              The reference helps us match your transfer instantly. The invoice number works perfectly.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px] text-slate-700">Date sent</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                max={todayISO}
                data-testid="mtsm-date"
              />
            </div>
            <div>
              <Label className="text-[12px] text-slate-700">Sending bank (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Barclays · Acc 1234"
                maxLength={120}
                data-testid="mtsm-notes"
              />
            </div>
          </div>

          <Button
            disabled={busy}
            onClick={submit}
            className="w-full h-11 rounded-full font-semibold"
            data-testid="mtsm-submit"
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : <><Send className="size-4" /> Notify our team <ArrowRight className="size-4" /></>}
          </Button>

          <p className="text-[11px] text-slate-500 text-center">
            We'll email + SMS our ops team immediately. You'll see a "Transfer reported" badge on this order.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
